import { useEffect, useState } from 'react';
import { api, type GameWeather, type League } from '../lib/api';

const IMPACT_TONE: Record<string, string> = {
  none: 'bg-white/15', low: 'bg-white/15', moderate: 'bg-amber', high: 'bg-red', severe: 'bg-red',
};

/** Kickoff-hour forecast at the stadium, as a strip of scoreboard cells. */
export function WeatherStrip({ gameId, league }: { gameId: string; league: League }) {
  const [w, setW] = useState<GameWeather | null>(null);
  useEffect(() => { api.gameWeather(gameId, league).then(setW).catch(() => setW({ error: 'unavailable' })); }, [gameId, league]);
  if (!w || w.error) return null;

  const cells: [string, string][] = w.indoors
    ? [['Roof', w.roof === 'dome' ? 'Dome' : 'Closed'], ['Conditions', 'Indoors'], ['Wind', 'None'], ['Precipitation', 'None']]
    : w.forecast_available === false
      ? [['Stadium', w.stadium ?? ''], ['Forecast', 'Too far out'], ['Roof', w.roof ?? 'Outdoors'], ['Conditions', 'Check nearer kickoff']]
      : [
          ['Temperature', w.temperature_f != null ? `${Math.round(w.temperature_f)}°F` : '--'],
          ['Wind', w.wind_mph != null ? `${Math.round(w.wind_mph)} mph${w.gust_mph ? ` G${Math.round(w.gust_mph)}` : ''}` : '--'],
          ['Rain chance', w.precipitation_probability != null ? `${Math.round(w.precipitation_probability)}%` : '--'],
          ['Impact on play', (w.impact?.level ?? 'none').replace(/^./, (c) => c.toUpperCase())],
        ];

  return (
    <div>
      <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
        <p className="kicker">Kickoff weather</p>
        <p className="meta">{w.stadium}</p>
      </div>
      <div className="relative overflow-hidden rounded-md bg-ink text-paper">
        <dl className="relative grid grid-cols-2 md:grid-cols-4">
          {cells.map(([label, value], i) => (
            <div key={label}
              className={`px-4 py-5 ${i % 2 === 1 ? 'border-l border-white/15' : ''} ${i >= 2 ? 'border-t border-white/15' : ''} ${i % 4 !== 0 ? 'md:border-l md:border-white/15' : 'md:border-l-0'} md:border-t-0`}>
              <dt className="meta !text-paper/70">{label}</dt>
              <dd className="score mt-1 text-[24px] leading-none">{value}</dd>
            </div>
          ))}
        </dl>
        {w.impact?.flags?.length ? (
          <p className={`flex flex-wrap gap-x-3 px-4 py-2 font-cond text-[14px] font-semibold uppercase tracking-[0.06em] ${IMPACT_TONE[w.impact.level] ?? 'bg-white/15'}`}>
            {w.impact.flags.map((f) => <span key={f}>{f.replace(/_/g, ' ')}</span>)}
          </p>
        ) : null}
        {w.note && <p className="border-t border-white/15 px-4 py-2 text-[12px] text-paper/70">{w.note}</p>}
      </div>
    </div>
  );
}
